import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/common";
import DataTable from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search } from "lucide-react";
import { toast } from "sonner";

export default function Scholarships() {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin", "admission_manager", "management"].includes(user.role);
  const [d, setD] = useState(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const load = () => api.get(`/scholarships?${new URLSearchParams({ status, search })}`).then(({ data }) => setD(data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line

  const save = async () => {
    try { await api.patch(`/scholarships/${sel.id}`, { status: sel.status, admin_notes: sel.admin_notes || "" }); toast.success("Application updated"); setSel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const columns = [
    { key: "application_number", label: "Application", render: (r) => <div><div className="font-semibold text-slate-800">{r.name}</div><div className="text-[11px] font-mono text-slate-400">{r.application_number} · {r.phone}</div></div> },
    { key: "preferred_course", label: "Course / College", render: (r) => <div className="text-xs"><div>{r.preferred_course || "—"}</div><div className="text-slate-400">{r.preferred_college}</div></div> },
    { key: "qualification", label: "Qualification" },
    { key: "district", label: "District" },
    { key: "counselor_name", label: "Counselor" },
    { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
    { key: "created_at", label: "Applied", render: (r) => <span className="text-xs">{fmtDate(r.created_at)}</span> },
  ];
  const stats = d?.stats || {};
  return (
    <div>
      <PageHeader title="Scholarship Applications" subtitle="Applications received through counselor referral links" testid="scholarships-header" />
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        {["total", ...(d?.statuses || [])].map((k) => (
          <button key={k} onClick={() => setStatus(k === "total" ? "" : k)} data-testid={`sch-stat-${k.toLowerCase().replace(/\s/g, "-")}`}
            className={`text-left rounded-xl border p-3 transition-colors ${(status || "total") === k ? "bg-[#312E81] text-white border-[#312E81]" : "bg-white border-slate-200"}`}>
            <div className="text-2xl font-heading font-extrabold">{stats[k] ?? 0}</div><div className="text-xs capitalize opacity-80">{k}</div>
          </button>
        ))}
      </div>
      <div className="relative max-w-sm mb-3"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search name / phone / number" className="pl-9 bg-white" data-testid="sch-search" /></div>
      <DataTable columns={columns} rows={d?.items ?? null} loading={!d} testid="scholarships-table" rowTestid="sch-row" onRowClick={(r) => setSel({ ...r })} />
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{sel?.name} · {sel?.application_number}</DialogTitle></DialogHeader>
          {sel && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                {[["Email", sel.email], ["Phone", sel.phone], ["State", sel.state], ["District", sel.district], ["Qualification", sel.qualification], ["Stream", sel.preferred_stream], ["Course", sel.preferred_course], ["College", sel.preferred_college]].map(([k, v]) => <div key={k}><div className="text-slate-400">{k}</div><div className="font-medium">{v || "—"}</div></div>)}
              </div>
              <div className="flex gap-3 text-xs">{sel.marks_card_url && <a className="text-[#4F46E5] underline" href={sel.marks_card_url} target="_blank" rel="noreferrer">Marks card</a>}{sel.entrance_scorecard_url && <a className="text-[#4F46E5] underline" href={sel.entrance_scorecard_url} target="_blank" rel="noreferrer">Entrance scorecard</a>}</div>
              <div><Label>Status</Label>
                <Select value={sel.status} onValueChange={(v) => setSel({ ...sel, status: v })} disabled={!canEdit}><SelectTrigger data-testid="sch-status"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{d.statuses.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Admin Notes</Label><Textarea value={sel.admin_notes || ""} onChange={(e) => setSel({ ...sel, admin_notes: e.target.value })} disabled={!canEdit} rows={3} data-testid="sch-notes" /></div>
            </div>
          )}
          {canEdit && <DialogFooter><Button onClick={save} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="sch-save">Save</Button></DialogFooter>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
